// src/components/Home.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  signInWithPopup, 
  signInWithRedirect, 
  GoogleAuthProvider, 
  FacebookAuthProvider,
  OAuthProvider,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';

function Home({ user }) {
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Check if user profile is complete, otherwise redirect to complete profile
  const checkUserProfile = useCallback(async (userId) => {
    try {
      const userRef = doc(db, "users", userId);
      const docSnap = await getDoc(userRef);
      
      if (!docSnap.exists()) {
        // No profile, they need to complete registration
        navigate('/complete-profile');
      } else {
        // User exists, check if profile is complete
        const userData = docSnap.data();
        
        if (!userData.licenseAgreed || !userData.country) {
          // Profile is incomplete, redirect to complete profile
          navigate('/complete-profile');
        } else {
          // Profile complete, go to documents
          navigate('/documents');
        }
      }
    } catch (error) {
      console.error("Error checking user profile:", error);
      setErrorMessage("Error checking your profile. Please try again.");
    }
  }, [navigate]);

  // Check if user is authenticated and redirect if appropriate
  useEffect(() => {
    if (user) {
      checkUserProfile(user.uid);
    }
  }, [user, checkUserProfile]);

  // Handle sign in with Google
  const handleGoogleSignIn = async () => {
    setErrorMessage('');
    setLoading(true);
    
    try {
      const provider = new GoogleAuthProvider();
      
      // Check if this is a mobile device
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      
      if (isMobile) {
        // Set persistence and flag for redirect auth
        localStorage.setItem('authRedirectPending', 'true');
        await setPersistence(auth, browserLocalPersistence);
        await signInWithRedirect(auth, provider);
      } else {
        // Use popup for desktop
        const result = await signInWithPopup(auth, provider);
        const user = result.user;
        await handleAuthSuccess(user);
      }
    } catch (error) {
      console.error("Error signing in with Google:", error);
      if (error.code === 'auth/popup-closed-by-user') {
        setErrorMessage("Google sign-in was canceled. Please try again.");
      } else if (error.code === 'auth/popup-blocked') {
        setErrorMessage("Sign-in popup was blocked by your browser. Please enable popups for this site.");
      } else {
        setErrorMessage("Error signing in: " + error.message);
      }
      setLoading(false);
    }
  };

  // Handle sign in with Facebook
  const handleFacebookSignIn = async () => {
    setErrorMessage('');
    setLoading(true);
    
    try {
      const provider = new FacebookAuthProvider();
      provider.addScope('email');
      provider.addScope('public_profile');
      
      // Check if this is a mobile device
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      
      if (isMobile) {
        localStorage.setItem('authRedirectPending', 'true');
        await setPersistence(auth, browserLocalPersistence);
        await signInWithRedirect(auth, provider);
      } else {
        const result = await signInWithPopup(auth, provider);
        const user = result.user;
        await handleAuthSuccess(user);
      }
    } catch (error) {
      console.error("Error signing in with Facebook:", error);
      if (error.code === 'auth/popup-closed-by-user') {
        setErrorMessage("Facebook sign-in was canceled. Please try again.");
      } else if (error.code === 'auth/popup-blocked') {
        setErrorMessage("Sign-in popup was blocked by your browser. Please enable popups for this site.");
      } else {
        setErrorMessage("Error signing in: " + error.message);
      }
      setLoading(false);
    }
  };

  // Handle sign in with Apple
  const handleAppleSignIn = async () => {
    setErrorMessage('');
    setLoading(true);
    
    try {
      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');
      
      // Check if this is a mobile device
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      
      if (isMobile) {
        localStorage.setItem('authRedirectPending', 'true');
        await setPersistence(auth, browserLocalPersistence);
        await signInWithRedirect(auth, provider);
      } else {
        const result = await signInWithPopup(auth, provider);
        const user = result.user;
        await handleAuthSuccess(user);
      }
    } catch (error) {
      console.error("Error signing in with Apple:", error);
      if (error.code === 'auth/popup-closed-by-user') {
        setErrorMessage("Apple sign-in was canceled. Please try again.");
      } else if (error.code === 'auth/popup-blocked') {
        setErrorMessage("Sign-in popup was blocked by your browser. Please enable popups for this site.");
      } else {
        setErrorMessage("Error signing in: " + error.message);
      }
      setLoading(false);
    }
  };

  // Common handler for successful authentication
  const handleAuthSuccess = async (user) => {
    try {
      // Check if this user already has a profile
      const userRef = doc(db, "users", user.uid);
      const docSnap = await getDoc(userRef);
      
      if (!docSnap.exists()) {
        // Create a new profile for this social login user
        const nameParts = user.displayName ? user.displayName.split(' ') : ['', ''];
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        
        await setDoc(userRef, {
          firstName,
          lastName,
          email: user.email || '',
          country: '',  // We'll need to collect this later
          licenseAgreed: false, // They need to explicitly agree
          registrationDate: Timestamp.now(),
          yearlyBoardCount: 0,
          lastResetDate: Timestamp.now(),
          authProvider: user.providerData[0]?.providerId || 'unknown'
        });
        
        // Redirect to complete profile
        navigate('/complete-profile');
      } else {
        // User exists, check if profile is complete
        const userData = docSnap.data();
        
        if (!userData.licenseAgreed || !userData.country) {
          // Profile is incomplete, redirect to complete profile
          navigate('/complete-profile');
        } else {
          // Profile complete, go to documents
          navigate('/documents');
        }
      }
    } catch (error) {
      console.error("Error handling auth success:", error);
      setErrorMessage("Error processing your account. Please try again.");
      setLoading(false);
    }
  };

  // Mobile-optimized button style
  const mobileButtonStyle = {
    fontSize: '18px',
    fontWeight: '600',
    height: '56px',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '8px',
    border: 'none',
    padding: '0 16px',
    appearance: 'none',
    cursor: 'pointer'
  };

  const buttonContainerStyle = {
    marginBottom: '10px'
  };

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="text-center mb-6">
        <h1 className="text-3xl font-bold">Welcome to Waka Folding Technology</h1>
      </div>
      
      <div className="bg-gray-100 p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-semibold mb-4">Build Your Own Folding Board</h2>
        <p className="mb-4">
          Join our community of DIY builders and create your own folding watersports board using our innovative composite hinge technology.
        </p>
        <p className="mb-4">
          To access our documentation and start building, please sign in with your account or create a new one.
        </p>
        
        {/* Error message display */}
        {errorMessage && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-center">
            {errorMessage}
          </div>
        )}

        {/* Authentication Buttons */}
        <div className="max-w-md mx-auto mt-8">
          {/* Google Sign In */}
          <div style={buttonContainerStyle}>
            <button 
              onClick={handleGoogleSignIn}
              disabled={loading}
              style={{
                ...mobileButtonStyle,
                backgroundColor: '#ffffff',
                color: '#757575',
                border: '1px solid #dadce0'
              }}
              className="shadow-sm hover:shadow"
            >
              <svg className="w-6 h-6 mr-3" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <span>Sign in with Google</span>
            </button>
          </div>
          
          {/* Facebook Sign In */}
          <div style={buttonContainerStyle}>
            <button 
              onClick={handleFacebookSignIn}
              disabled={loading}
              style={{
                ...mobileButtonStyle,
                backgroundColor: '#4267B2',
                color: '#FFFFFF'
              }}
            >
              <svg className="w-6 h-6 mr-3" fill="#FFFFFF" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              <span>Sign in with Facebook</span>
            </button>
          </div>
          
          {/* Apple Sign In */}
          <div>
            <button 
              onClick={handleAppleSignIn}
              disabled={loading}
              style={{
                ...mobileButtonStyle,
                backgroundColor: '#000000',
                color: '#FFFFFF'
              }}
            >
              <svg className="w-6 h-6 mr-3" fill="#FFFFFF" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701z"/>
              </svg>
              <span>Sign in with Apple</span>
            </button>
          </div>
        </div>
      </div>

      <div className="bg-gray-100 p-6 rounded-lg shadow-md mt-8">
        <h3 className="text-lg font-semibold mb-3">License Information</h3>
        <p>
          Our non-commercial license allows you to build up to three boards per year for personal use.
          Before registering, you can review our full license terms.
        </p>
        <div className="mt-4">
          <Link 
            to="/view-license" 
            target={isMobile ? "_blank" : "_self"}
            className="inline-flex items-center text-blue-600 hover:underline"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            View License Terms
          </Link>
        </div>
      </div>
      
      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-gray-100 p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold mb-3">Innovative Design</h3>
          <p>
            Our patent-pending composite hinge technology allows you to create a folding board without mechanical components that could rust or get compromised by sand and debris.
          </p>
        </div>
        <div className="bg-gray-100 p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold mb-3">DIY Community</h3>
          <p>
            Join other passionate builders creating their own folding boards for personal use. Share your experiences and get support from the community.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Home;